/**
 * P-03 产品管理（docs/03-UIUX.md §5）。
 *
 * 产品表规模 < 1,000，一次加载完不分页。列表展示产品名、slug、状态、
 * 套餐数量。点击产品行展开详情，Tab 切换套餐/版本/权益。
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../icons/Icon';
import { CardSkeleton, EmptyFirstRun, ErrorBlock } from '../components/DataState';
import { CopyButton } from '../components/CopyButton';
import { describeError } from '../api/client';
import { createProduct, listPlans, listProducts } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { ICON_SIZE } from '../tokens';
import { formatDate } from '../lib/format';
import type { Plan, Product } from '../api/types';

export function ProductsPage() {
  const products = useAsync(useCallback(() => listProducts(), []), []);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">产品管理</h1>
          <p className="page-head__sub">
            管理产品、套餐与权益配置。每个产品可以包含多个套餐，套餐定义了授权参数。
          </p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
            <Icon name="plus" size={ICON_SIZE.inline} />
            新建产品
          </button>
        </div>
      </div>

      {products.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(products.state.error, '加载产品列表')} onRetry={products.reload} />
        </div>
      ) : products.state.kind === 'loading' ? (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <CardSkeleton lines={3} />
          <CardSkeleton lines={3} />
          <CardSkeleton lines={3} />
        </div>
      ) : products.state.data.items.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="package"
            description="还没有产品。创建第一个产品来开始管理授权方案。"
            action={
              <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
                <Icon name="plus" size={ICON_SIZE.inline} />
                新建产品
              </button>
            }
          />
        </div>
      ) : (
        <div className="table-frame">
          <table className="table">
            <thead>
              <tr>
                <th className="table__cell--name">产品名称</th>
                <th className="table__cell--slug">标识</th>
                <th className="table__cell--status">状态</th>
                <th className="table__cell--count">套餐数</th>
                <th className="table__cell--date">创建时间</th>
                <th className="table__cell--action" />
              </tr>
            </thead>
            <tbody>
              {products.state.data.items.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  isExpanded={expandedId === product.id}
                  onToggle={() => setExpandedId(expandedId === product.id ? null : product.id)}
                  onSelect={() => setSelectedProduct(product)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedProduct !== null && (
        <ProductDetailPanel
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {showCreate && (
        <CreateProductModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); products.reload(); }}
        />
      )}
    </>
  );
}

interface CreateProductModalProps {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function CreateProductModal({ onClose, onCreated }: CreateProductModalProps) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!slug.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createProduct({ slug: slug.trim(), name: name.trim(), description: description.trim() || null });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">新建产品</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <div className="field">
            <label>标识 (slug)</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-product"
              pattern="[a-z0-9][a-z0-9-]*"
              maxLength={64}
            />
            <p className="text-muted" style={{ fontSize: 12 }}>
              小写字母/数字/连字符，如 &quot;office-pro&quot;
            </p>
          </div>

          <div className="field">
            <label>产品名称</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Office Pro"
              maxLength={120}
            />
          </div>

          <div className="field">
            <label>描述（可选）</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="产品用途说明"
            />
          </div>

          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}

          <button
            type="button"
            className="btn btn--primary"
            disabled={submitting || !slug.trim() || !name.trim()}
            onClick={handleSubmit}
          >
            {submitting ? '创建中…' : '创建产品'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ProductRowProps {
  readonly product: Product;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly onSelect: () => void;
}

function ProductRow({ product, isExpanded, onToggle, onSelect }: ProductRowProps) {
  return (
    <>
      <tr
        className="table__row-clickable"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
      >
        <td className="table__cell--name">
          <span className="cell-inline">
            <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size={ICON_SIZE.badge} />
            {product.name}
          </span>
        </td>
        <td className="table__cell--slug">
          <code>{product.slug}</code>
        </td>
        <td className="table__cell--status">
          <span className={`status-dot status-dot--${product.is_active ? 'active' : 'archived'}`} />
          {product.is_active ? '启用' : '已归档'}
        </td>
        <td className="table__cell--count">
          <PlansCount productId={product.id} />
        </td>
        <td className="table__cell--date">
          {formatDate(product.created_at)}
        </td>
        <td className="table__cell--action">
          <span className="row-actions">
            <CopyButton value={product.id} what="产品ID" />
            <Link
              to={`/products/${product.id}`}
              className="btn btn--quiet"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="eye" size={ICON_SIZE.inline} />
              详情
            </Link>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={(e) => { e.stopPropagation(); onSelect(); }}
            >
              管理
            </button>
          </span>
        </td>
      </tr>
      {isExpanded && (
        <tr className="table__expanded-row">
          <td colSpan={6}>
            <div className="product-expanded">
              <p className="product-expanded__desc">
                {product.description ?? '暂无描述'}
              </p>
              <div className="product-expanded__actions">
                <button type="button" className="btn btn--quiet" onClick={onSelect}>
                  <Icon name="settings-2" size={ICON_SIZE.inline} />
                  管理套餐与权益
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PlansCount({ productId }: { productId: string }) {
  const plans = useAsync(useCallback(() => listPlans(productId), [productId]), [productId]);
  if (plans.state.kind === 'loading') return <span className="skeleton-text" />;
  if (plans.state.kind === 'failed') return <span className="text-muted">-</span>;
  return <span>{plans.state.data.items.length}</span>;
}

interface ProductDetailPanelProps {
  readonly product: Product;
  readonly onClose: () => void;
}

function ProductDetailPanel({ product, onClose }: ProductDetailPanelProps) {
  const plans = useAsync(useCallback(() => listPlans(product.id), [product.id]), [product.id]);

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">{product.name}</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>

        <div className="panel__body">
          <div className="panel__section">
            <h3 className="panel__section-title">基本信息</h3>
            <dl className="detail-list">
              <dt>产品 ID</dt>
              <dd>
                <code>{product.id}</code>
                <CopyButton value={product.id} what="产品ID" />
              </dd>
              <dt>标识</dt>
              <dd><code>{product.slug}</code></dd>
              <dt>描述</dt>
              <dd>{product.description ?? '-'}</dd>
              <dt>状态</dt>
              <dd>{product.is_active ? '启用' : '已归档'}</dd>
              <dt>创建时间</dt>
              <dd>{formatDate(product.created_at)}</dd>
            </dl>
          </div>

          <div className="panel__section">
            <h3 className="panel__section-title">套餐列表</h3>
            {plans.state.kind === 'loading' ? (
              <CardSkeleton lines={2} />
            ) : plans.state.kind === 'failed' ? (
              <ErrorBlock message={describeError(plans.state.error, '加载套餐')} onRetry={plans.reload} />
            ) : plans.state.data.items.length === 0 ? (
              <p className="text-muted">暂无套餐</p>
            ) : (
              <div className="plan-cards">
                {plans.state.data.items.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan }: { plan: Plan }) {
  // 防御性 fallback：API 返回的 plan 若缺失字段（脏数据/契约漂移），渲染「—」而非 undefined/NaN
  const maxDevices = typeof plan.max_devices === 'number' ? plan.max_devices : '—';
  const duration = plan.duration_days === null
    ? '永久'
    : typeof plan.duration_days === 'number'
      ? `${plan.duration_days} 天`
      : '—';
  const grace = typeof plan.offline_grace_seconds === 'number' && Number.isFinite(plan.offline_grace_seconds)
    ? plan.offline_grace_seconds >= 86400
      ? `${Math.floor(plan.offline_grace_seconds / 86400)} 天`
      : `${Math.floor(plan.offline_grace_seconds / 3600)} 小时`
    : '—';
  return (
    <div className="plan-card">
      <div className="plan-card__head">
        <span className="plan-card__name">{plan.name}</span>
        <span className="plan-card__code"><code>{plan.code}</code></span>
        <span className={`status-dot status-dot--${plan.is_active ? 'active' : 'archived'}`} />
      </div>
      <div className="plan-card__body">
        <div className="plan-card__stat">
          <span className="plan-card__stat-value">{maxDevices}</span>
          <span className="plan-card__stat-label">设备上限</span>
        </div>
        <div className="plan-card__stat">
          <span className="plan-card__stat-value">{duration}</span>
          <span className="plan-card__stat-label">有效期</span>
        </div>
        <div className="plan-card__stat">
          <span className="plan-card__stat-value">{grace}</span>
          <span className="plan-card__stat-label">离线宽限</span>
        </div>
      </div>
      {plan.entitlements && Object.keys(plan.entitlements).length > 0 && (
        <div className="plan-card__entitlements">
          {Object.entries(plan.entitlements).map(([key, value]) => (
            <span key={key} className="tag">
              {key}: {String(value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}